import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { EVENT_STATUSES, type EventStatus } from '../events/labels';
import { SettlementPicker } from '../events/SettlementPicker';
import { ExpectedPaymentsTable } from './ExpectedPaymentsTable';
import { currentMonth, marginLabel, monthLabel, paymentsByForm, shiftMonth, sourceLabel, turnoverTotal } from './format';
import { TileList, type Tile } from './Tiles';
import { FINANCE_KEY, PROFIT_RESOURCE, type Cash, type MonthSummary, type TaskCosts, type Totals, type TransportLine, type YearSummary } from './types';
import { YearTable } from './YearTable';

const DEFAULT_STATUSES = (Object.keys(EVENT_STATUSES) as EventStatus[]).filter((s) => s !== 'anulowane' && s !== 'rezygnacja');
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);
const EVENTS_RESOURCE = 'events.events';

/** "1 faktura", "3 faktury", "5 faktur". */
export function plural(n: number, one: string, few: string, many: string): string {
  const tens = n % 100;
  const units = n % 10;
  return `${n} ${n === 1 ? one : units >= 2 && units <= 4 && (tens < 12 || tens > 14) ? few : many}`;
}

/**
 * F45: the summary's tiles — Szacowany dochód (F29's result), then the parts the reader may see (Obroty, Wpłaty, Brak
 * wpłat, Prognoza wpływów, Inwestycje), Koszty stałe and (owner 2026-10-22 01:34) Koszty zadań last.
 */
export function summaryTiles(t: Totals & Cash, year = false, fixedUnpaid?: number): Tile[] {
  const revenue = t.revenueConfirmedGrosze + t.revenueForecastGrosze;
  const tiles: Tile[] = [
    {
      label: 'Szacowany dochód',
      value: t.resultGrosze,
      negative: t.resultGrosze < 0,
      note: `przychód ${formatPln(revenue)}${t.revenueForecastGrosze > 0 ? ` (w tym prognoza ${formatPln(t.revenueForecastGrosze)})` : ''} · marża ${marginLabel(t.marginBp)}`,
      testId: 'tile-income',
    },
  ];
  if (t.turnover) {
    const total = turnoverTotal(t.turnover);
    const without = t.turnover.withoutInvoiceGrosze ?? 0;
    tiles.push({ label: 'Obroty', value: total, negative: total < 0, note: `netto, ${plural(t.turnover.invoices, 'faktura', 'faktury', 'faktur')}${without !== 0 ? ` + bez faktury ${formatPln(without)} (wg dnia wpłaty)` : ''}`, testId: 'tile-turnover' });
  }
  if (t.payments) {
    const forms = paymentsByForm(t.payments);
    tiles.push({
      label: 'Wpłaty',
      value: t.payments.totalGrosze,
      note: `przelew ${formatPln(t.payments.przelewGrosze)} · gotówka ${formatPln(t.payments.gotowkaGrosze)} · barter ${formatPln(t.payments.barterGrosze)}${forms ? ` — wg formy: ${forms}` : ''}`,
      testId: 'tile-payments',
    });
  }
  if (t.missing) {
    const count = t.missing.count > 0 ? plural(t.missing.count, 'zaległa płatność', 'zaległe płatności', 'zaległych płatności') : 'nic zaległego';
    // review M5: in the year it is a state (the end of the year, or today), not a sum of the months
    tiles.push({ label: 'Brak wpłat', value: t.missing.overdueGrosze, negative: t.missing.overdueGrosze > 0, note: year ? `stan na koniec roku / na dziś: ${count}` : count, testId: 'tile-missing' });
  }
  if (t.forecast)
    tiles.push({
      label: 'Prognoza wpływów',
      value: t.forecast.totalGrosze,
      note: `należności ${formatPln(t.forecast.receivablesGrosze)} · raty ${formatPln(t.forecast.installmentsGrosze)} · kosztorysy ${formatPln(t.forecast.quotesGrosze)}`,
      testId: 'tile-forecast',
    });
  if (t.investments) tiles.push({ label: 'Inwestycje', value: t.investments.netGrosze, note: t.catalogPurchases ? `zakupy do katalogu ${formatPln(t.catalogPurchases.netGrosze)}` : undefined, testId: 'tile-investments' });
  else if (t.catalogPurchases) tiles.push({ label: 'Zakupy do katalogu', value: t.catalogPurchases.netGrosze, testId: 'tile-catalog' });
  // owner 2026-10-24: the month's fixed costs not paid (only for whoever sees the fixed costs)
  tiles.push({ label: 'Koszty stałe', value: t.fixedCostsGrosze, ...(fixedUnpaid !== undefined ? { note: `nieopłacone: ${fixedUnpaid}` } : {}), testId: 'tile-fixed' });
  if (t.taskCostsGrosze !== undefined) tiles.push({ label: 'Koszty zadań', value: t.taskCostsGrosze, note: `zrobione; zaplanowane ${formatPln(t.taskCostsPlannedGrosze ?? 0)}`, testId: 'tile-tasks' });
  return tiles;
}

/**
 * Owner 2026-11-04 (decision a): the period's own transport — the actual „Paliwo” when the month has any, else the
 * events' „Transport własny” estimates; a year with both kinds of months is „mieszane”.
 */
export function transportLine(t: TransportLine): { label: string; note: string } {
  if (t.basis === 'rzeczywiste') return { label: 'Paliwo (rzeczywiste)', note: `zamiast szacunku transportu własnego ${formatPln(t.estimateGrosze)}` };
  if (t.basis === 'szacunek') return { label: 'Transport (szacunek)', note: 'bez wpisanego paliwa: szacunek z km wydarzeń' };
  return { label: 'Paliwo i transport', note: `paliwo rzeczywiste ${formatPln(t.fuelGrosze)} w miesiącach z paliwem, w pozostałych szacunek` };
}

function MonthTable({ data, canOpen, me }: { data: MonthSummary; canOpen: boolean; me: Me }) {
  const navigate = useNavigate();
  // owner 2026-11-04: „Rozliczenie” as a column of its own, the last one (11:23 „na koncu”; rule 7a: none without the field); `w` widens the empty row and the footer's trailing cells
  const settlement = fieldVisible(me, EVENTS_RESOURCE, 'settlement');
  const w = settlement ? 1 : 0;
  const open = (id: string) => navigate(`/wydarzenia?wydarzenie=${id}&zakladka=rentownosc`);
  const t = data.totals;
  return (
    <div className="table-wrap">
      <table className="table fin-table" aria-label={`Wydarzenia: ${monthLabel(data.month)}`}>
        <thead>
          <tr>
            <th scope="col">Data</th>
            <th scope="col">Wydarzenie</th>
            <th scope="col">Przychód</th>
            <th scope="col" className="num">Kwota</th>
            <th scope="col" className="num">Koszty</th>
            <th scope="col" className="num">Wynik</th>
            <th scope="col" className="num">Marża</th>
            {settlement && <th scope="col">Rozliczenie</th>}
          </tr>
        </thead>
        <tbody>
          {data.events.length === 0 && (
            <tr>
              <td colSpan={7 + w} className="muted">
                Brak wydarzeń w tym miesiącu.
              </td>
            </tr>
          )}
          {data.events.map((e) => (
            <tr key={e.eventId} className={canOpen ? 'clickable' : undefined} onClick={canOpen ? () => open(e.eventId) : undefined}>
              <td>{new Date(e.startsAt).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric' })}</td>
              <td>
                {canOpen ? (
                  <a
                    href={`/wydarzenia?wydarzenie=${e.eventId}&zakladka=rentownosc`}
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      open(e.eventId);
                    }}
                  >
                    {e.name}
                  </a>
                ) : (
                  e.name
                )}{' '}
                <span className="status" data-status={e.status}>
                  {EVENT_STATUSES[e.status as EventStatus] ?? e.status}
                </span>
              </td>
              <td className={e.forecast ? 'fin-forecast' : undefined}>{sourceLabel(e.source)}</td>
              <td className="num">{formatPln(e.revenueGrosze)}</td>
              <td className="num">{formatPln(e.costGrosze)}</td>
              <td className={`num${e.resultGrosze < 0 ? ' neg' : ''}`}>{formatPln(e.resultGrosze)}</td>
              <td className="num">{marginLabel(e.marginBp)}</td>
              {settlement && (
                // choosing in the select must not open the event (the row is clickable)
                <td onClick={(ev) => ev.stopPropagation()}>
                  <SettlementPicker me={me} eventId={e.eventId} eventName={e.name} version={e.eventVersion} value={e.settlement} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={3}>
              {t.transport ? 'Razem wydarzenia (bez transportu własnego)' : 'Razem wydarzenia'}
            </th>
            <td className="num">{formatPln(t.revenueConfirmedGrosze + t.revenueForecastGrosze)}</td>
            <td className="num">{formatPln(t.eventCostsGrosze)}</td>
            <td className="num">{formatPln(t.revenueConfirmedGrosze + t.revenueForecastGrosze - t.eventCostsGrosze)}</td>
            <td />
            {settlement && <td />}
          </tr>
          {t.transport && (
            <tr data-testid="month-transport">
              <th scope="row" colSpan={4}>
                {transportLine(t.transport).label} <span className="fin-month-note">{transportLine(t.transport).note}</span>
              </th>
              <td className="num">{formatPln(t.transport.transportGrosze)}</td>
              <td colSpan={2 + w} />
            </tr>
          )}
          <tr>
            <th scope="row" colSpan={4}>
              Koszty stałe
            </th>
            <td className="num">{formatPln(t.fixedCostsGrosze)}</td>
            <td colSpan={2 + w} />
          </tr>
          {t.taskCostsGrosze !== undefined && (
            <tr>
              <th scope="row" colSpan={4}>
                Koszty zadań (zrobione)
              </th>
              <td className="num">{formatPln(t.taskCostsGrosze)}</td>
              <td colSpan={2 + w} />
            </tr>
          )}
          <tr>
            <th scope="row" colSpan={5}>
              Wynik firmy
            </th>
            <td className={`num${t.resultGrosze < 0 ? ' neg' : ''}`} data-testid="month-result">
              {formatPln(t.resultGrosze)}
            </td>
            <td className="num">{marginLabel(t.marginBp)}</td>
            {settlement && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** The month's company tasks with a cost (owner 2026-10-22 01:34): done ones counted, open ones planned. */
function TaskCostsTable({ data, month }: { data: TaskCosts; month: string }) {
  const navigate = useNavigate();
  const withCategory = data.tasks!.some((t) => t.costCategory !== undefined);
  return (
    <section className="fin-section" aria-label="Koszty zadań">
      <h3>Koszty zadań</h3>
      <div className="table-wrap">
        <table className="table fin-table" aria-label={`Koszty zadań: ${monthLabel(month)}`}>
          <thead>
            <tr>
              <th scope="col">Termin</th>
              <th scope="col">Zadanie</th>
              {withCategory && <th scope="col">Rodzaj kosztu</th>}
              <th scope="col">Stan</th>
              <th scope="col" className="num">Koszt netto</th>
            </tr>
          </thead>
          <tbody>
            {data.tasks!.map((t) => (
              <tr key={t.taskId} className="clickable" onClick={() => navigate(`/zadania?zadanie=${t.taskId}`)}>
                <td>{`${Number(t.dueDay.slice(8))}.${t.dueDay.slice(5, 7)}`}</td>
                <td>
                  <a
                    href={`/zadania?zadanie=${t.taskId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/zadania?zadanie=${t.taskId}`);
                    }}
                  >
                    {t.title}
                  </a>
                </td>
                {withCategory && <td>{t.costCategory || '—'}</td>}
                <td className={t.done ? undefined : 'fin-forecast'}>{t.done ? 'Zrobione' : 'Zaplanowane'}</td>
                <td className="num">{formatPln(t.costGrosze)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" colSpan={withCategory ? 4 : 3}>
                Zrobione (w wyniku) · zaplanowane
              </th>
              <td className="num">
                {formatPln(data.doneGrosze)} · {formatPln(data.plannedGrosze)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

/** "Finanse → Podsumowanie": a month (tiles, "Oczekiwane płatności", the events) or a year (12 months). */
export function SummaryPage({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const view = params.get('widok') === 'rok' ? 'rok' : 'miesiac';
  const month = /^\d{4}-\d{2}$/.test(params.get('miesiac') ?? '') ? params.get('miesiac')! : currentMonth();
  const year = Number(month.slice(0, 4));
  const [statuses, setStatuses] = useState<EventStatus[]>(DEFAULT_STATUSES);
  const set = (next: Record<string, string>) => setParams((p) => ({ ...Object.fromEntries(p), ...next }), { replace: true });
  const q = `statuses=${statuses.join(',')}`;
  const monthData = useQuery({ queryKey: [...FINANCE_KEY, 'month', month, q], queryFn: () => api<MonthSummary>(`/api/finance/summary/month?month=${month}&${q}`), enabled: view === 'miesiac' && statuses.length > 0 });
  const yearData = useQuery({ queryKey: [...FINANCE_KEY, 'year', year, q], queryFn: () => api<YearSummary>(`/api/finance/summary/year?year=${year}&${q}`), enabled: view === 'rok' && statuses.length > 0 });
  const data = view === 'rok' ? yearData : monthData;
  const totals: (Totals & Cash) | undefined = view === 'rok' ? yearData.data?.totals : monthData.data ? { ...monthData.data, ...monthData.data.totals } : undefined;
  return (
    <section aria-label="Zestawienie" data-feature-id="finance.summaries">
      <div className="fin-toolbar">
        <div className="segment" role="group" aria-label="Okres">
          <button type="button" aria-pressed={view === 'miesiac'} onClick={() => set({ widok: 'miesiac' })}>
            Miesiąc
          </button>
          <button type="button" aria-pressed={view === 'rok'} onClick={() => set({ widok: 'rok' })}>
            Rok
          </button>
        </div>
        <button className="btn ghost small" type="button" aria-label="Poprzedni okres" onClick={() => set({ miesiac: shiftMonth(month, view === 'rok' ? -12 : -1) })}>
          ‹
        </button>
        <span className="fin-period" aria-live="polite">
          {view === 'rok' ? `Rok ${year}` : monthLabel(month)}
        </span>
        <button className="btn ghost small" type="button" aria-label="Następny okres" onClick={() => set({ miesiac: shiftMonth(month, view === 'rok' ? 12 : 1) })}>
          ›
        </button>
      </div>
      <fieldset className="fin-statuses">
        <legend className="visually-hidden">Statusy wydarzeń</legend>
        {(Object.keys(EVENT_STATUSES) as EventStatus[]).map((s) => (
          <label key={s}>
            <input type="checkbox" checked={statuses.includes(s)} onChange={(e) => setStatuses(e.target.checked ? [...statuses, s] : statuses.filter((x) => x !== s))} />
            {EVENT_STATUSES[s]}
          </label>
        ))}
      </fieldset>
      {statuses.length === 0 && <p className="muted">Zaznacz co najmniej jeden status.</p>}
      {data.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {data.error && <p className="alert" role="alert">{errorText(data.error, 'Nie udało się wczytać zestawienia')}</p>}
      {totals && <TileList label={view === 'rok' ? `Rok ${year} w liczbach` : `${monthLabel(month)} w liczbach`} tiles={summaryTiles(totals, view === 'rok', view === 'miesiac' ? monthData.data?.fixedUnpaid : undefined)} />}
      {view === 'miesiac' && monthData.data?.expected && <ExpectedPaymentsTable rows={monthData.data.expected} canOpen={can(me, EVENTS_RESOURCE, 'view')} />}
      {view === 'miesiac' && monthData.data && (
        <section className="fin-section" aria-label="Wydarzenia miesiąca">
          <h3>Wydarzenia</h3>
          <MonthTable data={monthData.data} canOpen={can(me, PROFIT_RESOURCE, 'view')} me={me} />
        </section>
      )}
      {view === 'miesiac' && monthData.data?.taskCosts?.tasks && monthData.data.taskCosts.tasks.length > 0 && <TaskCostsTable data={monthData.data.taskCosts} month={monthData.data.month} />}
      {view === 'rok' && yearData.data && <YearTable data={yearData.data} onMonth={(m) => set({ widok: 'miesiac', miesiac: m })} />}
      <p className="hint">Wydarzenia liczą się w miesiącu swojego pierwszego dnia. Przychód bez zaakceptowanej wersji kosztorysu to prognoza. Szacowany dochód = przychód − koszty wydarzeń − koszty stałe − koszty zrobionych zadań firmowych (zadania liczą się w miesiącu terminu; niezrobione to koszty zaplanowane). Obroty – faktury wystawione w miesiącu; wpłaty – według dnia wpłaty.</p>
    </section>
  );
}
